import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import ResmoMascot from "../components/ResmoMascot";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Sparkles, ChevronRight, X, Plus, Loader, Trash2, ArrowLeft,
  TrendingUp, MapPin, Briefcase, DollarSign, Target, Lightbulb, AlertCircle,
  Search, Compass, Footprints,
} from "lucide-react";
import { RoleResearchList, RoleResearchWizard, RoleResearchResult } from "./RoleResearch";
import AdjacentRoleJobsPanel from "../components/AdjacentRoleJobsPanel";
import ResultSection from "../components/ResultSection";
import SectionExpandToolbar from "../components/SectionExpandToolbar";
import { ReportTabStrip, ScoreRow, ScoreDetail } from "../components/reports/ScoreAnalysis";

const RATING_LABELS = ["", "Novice", "Learning", "Capable", "Strong", "Expert"];

const RatingPicker = ({ value, onChange, testId }) => (
  <div className="flex gap-1.5" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="w-9 h-9 rounded-md font-display flex items-center justify-center transition-transform hover:scale-105"
        style={{
          background: n <= value ? "#FF6A00" : "white",
          color: n <= value ? "white" : "#0F1B2D",
          border: "3px solid #0F1B2D",
          boxShadow: n <= value ? "2px 2px 0 0 #0F1B2D" : "none",
        }}
        data-testid={`${testId}-${n}`}
        title={RATING_LABELS[n]}
      >
        {n}
      </button>
    ))}
    <span className="ml-2 self-center font-mono-rm text-xs uppercase tracking-widest" style={{ color: "#475569" }}>
      {RATING_LABELS[value] || "—"}
    </span>
  </div>
);

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "INR"];

function SkillsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/skills/assessments");
      setItems(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this assessment?")) return;
    try {
      await api.delete(`/skills/assessments/${id}`);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const fmt = (n, c) => n ? `${c} ${Math.round(n).toLocaleString()}` : "—";

  return (
    <div>
      <div className="mb-8">
        <div className="rm-tag mb-3">Skill Lab</div>
        <h1 className="font-display text-4xl md:text-5xl uppercase">
          Map your <span className="rm-text-orange">career.</span>
        </h1>
        <p className="text-base md:text-lg mt-2 max-w-2xl" style={{ color: "#475569" }}>
          Two tools, one lab. Find out what your skills are worth — or research any role
          to see the path, pay tiers, and skills you'll need.
        </p>
      </div>

      {/* Three CTA cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-testid="skill-lab-hub">
        <div
          className="rm-card p-6 md:p-7 flex flex-col"
          style={{ background: "#FFFBEB" }}
          data-testid="hub-card-worth"
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "#FF6A00", color: "white", border: "3px solid #0F1B2D" }}
            >
              <DollarSign size={22} strokeWidth={2.5} />
            </div>
            <div>
              <div className="rm-label">Salary assessment</div>
              <h2 className="font-display text-xl uppercase leading-tight">What am I worth?</h2>
            </div>
          </div>
          <p className="text-sm mb-5" style={{ color: "#475569" }}>
            Rate your own skills, get back a market-grounded salary range and a
            negotiation playbook.
          </p>
          <button
            onClick={() => navigate("/skills/new")}
            className="rm-btn rm-btn-primary mt-auto self-start"
            data-testid="new-assessment-btn"
          >
            <Sparkles size={16} /> Start assessment <ChevronRight size={16} />
          </button>
        </div>

        <div
          className="rm-card p-6 md:p-7 flex flex-col"
          style={{ background: "#0F1B2D", color: "white" }}
          data-testid="hub-card-role"
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "#20C1A7", color: "#0F1B2D", border: "3px solid #20C1A7" }}
            >
              <Compass size={22} strokeWidth={2.5} />
            </div>
            <div>
              <div className="rm-label" style={{ color: "#FFC145" }}>Role research</div>
              <h2 className="font-display text-xl uppercase leading-tight">Research a role.</h2>
            </div>
          </div>
          <p className="text-sm mb-5" style={{ color: "#CBD5E1" }}>
            Type any title — get the day-to-day, skills required, salary by experience
            tier, certifications, and where it leads.
          </p>
          <button
            onClick={() => navigate("/skills/role/new")}
            className="rm-btn rm-btn-secondary mt-auto self-start"
            style={{ background: "#FF6A00", color: "white", borderColor: "#FF6A00" }}
            data-testid="new-role-research-btn"
          >
            <Search size={16} /> Research a role <ChevronRight size={16} />
          </button>
        </div>

        <div
          className="rm-card p-6 md:p-7 flex flex-col"
          style={{ background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)" }}
          data-testid="hub-card-network"
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "#0F1B2D", color: "#FFC145", border: "3px solid #0F1B2D" }}
            >
              <Footprints size={22} strokeWidth={2.5} />
            </div>
            <div>
              <div className="rm-label">Stepping Stones · NEW</div>
              <h2 className="font-display text-xl uppercase leading-tight">Build your network.</h2>
            </div>
          </div>
          <p className="text-sm mb-5" style={{ color: "#475569" }}>
            Elevator pitches, contact graph, conversation sparks, mindset
            coaching, event finders — your full networking arsenal in one
            place. Each contact is a stepping stone on your path.
          </p>
          <button
            onClick={() => navigate("/network")}
            className="rm-btn mt-auto self-start"
            style={{ background: "#0F1B2D", color: "#FFC145", border: "3px solid #0F1B2D", boxShadow: "2px 2px 0 0 #0F1B2D", fontWeight: 700 }}
            data-testid="new-network-btn"
          >
            <Footprints size={16} /> Open Network <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 mb-6 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}>
          {error}
        </div>
      )}

      {/* Recent salary assessments */}
      {loading ? (
        <div className="text-center py-10 font-mono-rm">Loading...</div>
      ) : items.length === 0 ? null : (
        <div className="mb-2">
          <div className="rm-label mb-3">Recent salary assessments</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="skills-grid">
            {items.map((it) => (
              <div key={it.id} className="rm-card p-6" data-testid={`assessment-card-${it.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl uppercase leading-tight truncate">
                      {it.target_role || "Untitled role"}
                    </div>
                    <div className="font-mono-rm text-xs uppercase tracking-widest mt-1" style={{ color: "#475569" }}>
                      {it.location || "—"} {it.years_experience ? `· ${it.years_experience}y exp` : ""}
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 rounded-md" style={{ border: "3px solid #0F1B2D", background: "#FFFBEB" }}>
                  <div className="rm-label mb-1">Range</div>
                  <div className="font-display text-lg leading-tight">
                    {fmt(it.low, it.currency)} <span style={{ color: "#475569" }}>—</span> {fmt(it.high, it.currency)}
                  </div>
                  <div className="font-mono-rm text-xs mt-1" style={{ color: "#475569" }}>mid {fmt(it.mid, it.currency)}</div>
                </div>
                <div className="font-mono-rm text-xs mt-3" style={{ color: "#475569" }}>
                  {it.skill_count} skills rated · {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
                </div>
                <div className="flex gap-2 mt-4">
                  <Link to={`/skills/${it.id}`} className="rm-btn rm-btn-primary text-xs px-3 py-2 flex-1" data-testid={`view-assessment-${it.id}`}>
                    Open
                  </Link>
                  <button onClick={() => handleDelete(it.id)} className="rm-btn rm-btn-ghost text-xs px-3 py-2" data-testid={`delete-assessment-${it.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent role research */}
      <RoleResearchList />

      {/* Empty state if both lists are empty */}
      {!loading && items.length === 0 && (
        <div className="text-center py-12" data-testid="skills-empty">
          <ResmoMascot state="idle" size={160} />
          <div className="font-display text-2xl uppercase mt-4">Pick a tool above to get started.</div>
          <p className="font-mono-rm text-sm mt-2" style={{ color: "#475569" }}>
            Resmo works fastest with specifics.
          </p>
        </div>
      )}
    </div>
  );
}

function SkillsWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=extract+rate, 2=context, 3=submitting
  const [extracting, setExtracting] = useState(false);
  const [skills, setSkills] = useState([]); // {name, category, evidence, rating}
  const [newSkill, setNewSkill] = useState("");
  const [error, setError] = useState("");

  const [targetRole, setTargetRole] = useState("");
  const [location, setLocation] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [currency, setCurrency] = useState("USD");
  // Optional current salary — used for market-vs-current delta in the result.
  // Pre-filled from the user's profile (one-time prompt; per-session override).
  const [currentSalary, setCurrentSalary] = useState("");
  const [saveSalaryToProfile, setSaveSalaryToProfile] = useState(true);

  // Pull the user's saved salary from their profile so the field pre-fills.
  // No-op if they've never set it.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        const cs = (data?.profile || {}).current_salary;
        if (cs && !currentSalary) setCurrentSalary(String(cs));
      } catch { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState("");
  const [elapsed, setElapsed] = useState(0);

  // Elapsed timer while submitting
  useEffect(() => {
    if (!submitting) return;
    const start = Date.now();
    setElapsed(0);
    const t = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [submitting]);

  const extractFromResume = async () => {
    setError("");
    setExtracting(true);
    try {
      const { data } = await api.post("/skills/extract");
      setSkills((data.skills || []).map((s) => ({ ...s, rating: 3 })));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setExtracting(false);
    }
  };

  // Auto-extract on first mount
  useEffect(() => { extractFromResume(); /* eslint-disable-next-line */ }, []);

  const setRating = (idx, val) => {
    setSkills((arr) => arr.map((s, i) => (i === idx ? { ...s, rating: val } : s)));
  };
  const removeSkill = (idx) => setSkills((arr) => arr.filter((_, i) => i !== idx));
  const addSkill = () => {
    const name = newSkill.trim();
    if (!name) return;
    if (skills.some((s) => s.name.toLowerCase() === name.toLowerCase())) {
      setNewSkill("");
      return;
    }
    setSkills((arr) => [...arr, { name, category: "custom", evidence: "Added by you", rating: 3 }]);
    setNewSkill("");
  };

  const submit = async () => {
    setError("");
    if (skills.length < 3) {
      setError("Rate at least 3 skills first.");
      return;
    }
    setSubmitting(true);
    setStage("queued");
    try {
      const { data: jobData } = await api.post("/skills/assessment", {
        skills: skills.map((s) => ({ name: s.name, rating: s.rating })),
        target_role: targetRole || null,
        location: location || null,
        years_experience: yearsExperience ? parseInt(yearsExperience, 10) : null,
        currency,
        current_salary: currentSalary ? parseInt(currentSalary, 10) : null,
        save_to_profile: saveSalaryToProfile,
      });
      const jobId = jobData.job_id;
      const pollUntil = Date.now() + 1000 * 60 * 4;
      while (Date.now() < pollUntil) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: status } = await api.get(`/jobs/${jobId}`);
          if (status.stage) setStage(status.stage);
          if (status.status === "complete" && status.assessment_id) {
            navigate(`/skills/${status.assessment_id}`);
            return;
          }
          if (status.status === "error") {
            setSubmitting(false);
            setError(status.error || "Assessment failed.");
            return;
          }
        } catch {
          /* keep polling */
        }
      }
      setSubmitting(false);
      setError("Resmo took too long. Try again in a moment.");
    } catch (e) {
      setSubmitting(false);
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (submitting) {
    const stageMap = { queued: "Lining up the data", analyzing: "Crunching market signals", saving: "Plating your range", complete: "Done!" };
    return (
      <div className="text-center py-16 max-w-2xl mx-auto" data-testid="skills-submitting">
        <ResmoMascot state="chewing" size={220} />
        <div className="font-display text-3xl uppercase mt-6">
          Resmo is <span className="rm-text-orange">doing math.</span>
        </div>
        <div className="font-mono-rm text-sm mt-2 uppercase tracking-widest" style={{ color: "#475569" }}>
          {stageMap[stage] || "Processing..."}
        </div>
        <div className="font-display text-2xl mt-4" data-testid="skills-progress-percent">
          {Math.min(95, Math.round(8 + elapsed * 2.5))}<span className="text-base" style={{ color: "#475569" }}>% complete</span>
        </div>
        <div
          className="mt-4 h-3 rounded-full overflow-hidden mx-auto max-w-sm"
          style={{ background: "#FFF2EC", border: "2px solid #0F1B2D" }}
        >
          <div className="h-full transition-all" style={{ width: `${Math.min(95, 8 + elapsed * 2.5)}%`, background: "#FF6A00" }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate("/skills")} className="rm-tag mb-3" data-testid="wizard-back-btn">
        <ArrowLeft size={14} /> Skill Lab
      </button>
      <h1 className="font-display text-4xl md:text-5xl uppercase mb-2">
        Step <span className="rm-text-orange">{step}</span> of 2
      </h1>

      {step === 1 && (
        <>
          <p className="text-base md:text-lg max-w-2xl mb-6" style={{ color: "#475569" }}>
            Skills pulled from your resume — rate each 1 (novice) to 5 (expert). Be honest;
            the salary range is only as good as your inputs.
          </p>

          {extracting ? (
            <div className="text-center py-12">
              <Loader size={32} className="animate-spin mx-auto" />
              <div className="font-mono-rm text-sm mt-3 uppercase tracking-widest">Extracting from your resume...</div>
            </div>
          ) : (
            <>
              <div className="rm-card-static p-6 md:p-8 space-y-3" data-testid="skills-rating-list">
                {skills.length === 0 && (
                  <div className="font-mono-rm text-sm py-6 text-center" style={{ color: "#475569" }}>
                    No skills extracted. Add some manually below, or feed me a more detailed resume.
                  </div>
                )}
                {skills.map((s, i) => (
                  <div
                    key={s.name || i}
                    className="flex flex-wrap items-center gap-3 p-3 rounded-md"
                    style={{ border: "3px solid #0F1B2D", background: "white" }}
                    data-testid={`skill-row-${i}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm">{s.name}</div>
                      {s.evidence && (
                        <div className="font-mono-rm text-xs italic mt-0.5" style={{ color: "#475569" }}>
                          {s.evidence}
                        </div>
                      )}
                    </div>
                    <RatingPicker value={s.rating} onChange={(v) => setRating(i, v)} testId={`rating-${i}`} />
                    <button
                      onClick={() => removeSkill(i)}
                      className="rm-btn rm-btn-ghost text-xs px-2 py-2"
                      data-testid={`remove-skill-${i}`}
                      title="Remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}

                <div className="flex flex-wrap items-center gap-3 mt-4 pt-4" style={{ borderTop: "3px dashed #0F1B2D" }}>
                  <input
                    className="rm-input flex-1"
                    style={{ minWidth: 220 }}
                    placeholder="Add a skill (e.g., Kubernetes, Negotiation)"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())}
                    data-testid="add-skill-input"
                  />
                  <button onClick={addSkill} className="rm-btn rm-btn-secondary text-xs px-4 py-2" data-testid="add-skill-btn">
                    <Plus size={14} /> Add
                  </button>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="step1-error">
                  {error}
                </div>
              )}

              <div className="flex justify-between mt-6 flex-wrap gap-3">
                <button onClick={extractFromResume} className="rm-btn rm-btn-ghost text-xs" data-testid="re-extract-btn">
                  Re-extract from resume
                </button>
                <button
                  onClick={() => skills.length >= 3 ? setStep(2) : setError("Rate at least 3 skills first.")}
                  className="rm-btn rm-btn-primary"
                  data-testid="step1-next-btn"
                >
                  Next: target role <ChevronRight size={16} />
                </button>
              </div>
            </>
          )}
        </>
      )}

      {step === 2 && (
        <>
          <p className="text-base md:text-lg max-w-2xl mb-6" style={{ color: "#475569" }}>
            Tell Resmo what role you're aiming at and where. The more specific, the better the range.
          </p>

          <div className="rm-card-static p-6 md:p-8 space-y-5">
            <div>
              <label className="rm-label block mb-2 flex items-center gap-2"><Briefcase size={12} /> Target role</label>
              <input
                className="rm-input"
                placeholder="Senior Product Manager, Staff Engineer, Director of Marketing..."
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
                data-testid="target-role-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="rm-label block mb-2 flex items-center gap-2"><MapPin size={12} /> Location</label>
                <input
                  className="rm-input"
                  placeholder="San Francisco, NYC, Remote US, London..."
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  data-testid="location-input"
                />
              </div>
              <div>
                <label className="rm-label block mb-2">Currency</label>
                <select
                  className="rm-input"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  data-testid="currency-select"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="rm-label block mb-2">Years of experience</label>
              <input
                type="number"
                min="0"
                max="60"
                className="rm-input"
                placeholder="e.g. 8"
                value={yearsExperience}
                onChange={(e) => setYearsExperience(e.target.value)}
                style={{ maxWidth: 200 }}
                data-testid="years-input"
              />
            </div>

            <div>
              <label className="rm-label block mb-2">
                Current salary ({currency} · annual, gross)
                <span className="ml-2 font-mono-rm text-[10px] opacity-70 normal-case tracking-normal">
                  optional — used to compute market delta
                </span>
              </label>
              <input
                type="number"
                min="0"
                max="10000000"
                step="1000"
                className="rm-input"
                placeholder="e.g. 85000"
                value={currentSalary}
                onChange={(e) => setCurrentSalary(e.target.value)}
                style={{ maxWidth: 240 }}
                data-testid="current-salary-input"
              />
              {currentSalary && (
                <label className="inline-flex items-center gap-2 text-xs mt-2 cursor-pointer font-mono-rm"
                       style={{ color: "#475569" }}>
                  <input
                    type="checkbox"
                    checked={saveSalaryToProfile}
                    onChange={(e) => setSaveSalaryToProfile(e.target.checked)}
                    style={{ width: 14, height: 14, accentColor: "#FF6A00" }}
                    data-testid="save-salary-to-profile"
                  />
                  Remember this on my profile (pre-fill next time)
                </label>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="step2-error">
              {error}
            </div>
          )}

          <div className="flex justify-between mt-6 flex-wrap gap-3">
            <button onClick={() => setStep(1)} className="rm-btn rm-btn-ghost text-xs" data-testid="step2-back-btn">
              <ArrowLeft size={14} /> Back to skills
            </button>
            <button onClick={submit} className="rm-btn rm-btn-primary" data-testid="submit-assessment-btn">
              <Sparkles size={16} /> Get my range
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function SkillsResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  // Scroll a result section into view + auto-expand it (sections collapse
  // closed by default; the headline CTAs need to surface what they target).
  const scrollToSection = (id) => {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (!el) return;
    const toggle = el.querySelector(`[data-testid="${id}-toggle"]`);
    const body = el.querySelector(`[data-testid="${id}-body"]`);
    if (toggle && body && body.getAttribute("aria-hidden") === "true") {
      toggle.click();
    }
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/skills/assessments/${id}`);
        setData(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id]);

  const [skillSel, setSkillSel] = useState(null);
  const [adjSel, setAdjSel] = useState(null);

  const fmt = (n, c) => n ? `${c} ${Math.round(n).toLocaleString()}` : "—";

  const positions = useMemo(() => {
    if (!data) return null;
    const sr = data.result?.salary_range || {};
    const lo = sr.low || 0, hi = sr.high || 0, mid = sr.mid || 0;
    if (hi <= lo) return null;
    return { midPct: Math.min(100, Math.max(0, ((mid - lo) / (hi - lo)) * 100)) };
  }, [data]);

  if (error) {
    return (
      <div className="text-center py-16">
        <ResmoMascot state="hungry" size={160} />
        <div className="font-display text-2xl mt-3 uppercase">Couldn't load that assessment</div>
        <div className="font-mono-rm text-sm mt-2">{error}</div>
        <button onClick={() => navigate("/skills")} className="rm-btn rm-btn-primary mt-6">Back</button>
      </div>
    );
  }
  if (!data) return <div className="text-center py-16 font-mono-rm">Loading...</div>;

  const r = data.result || {};
  const sr = r.salary_range || {};
  const currency = sr.currency || data.currency || "USD";
  const ctx = r.market_context || {};
  const skillValue = r.skill_value || [];
  const topSkills = r.top_value_skills || [];
  const develop = r.develop_to_tier_up || [];
  const negotiation = r.negotiation_points || [];
  const redFlags = r.red_flags || [];
  const adjacentRoles = r.adjacent_roles || [];

  const demandColor = {
    hot: "#EF4444", strong: "#10B981", steady: "#FFC145", soft: "#94A3B8",
  }[ctx.demand] || "#94A3B8";

  return (
    <div data-testid="skills-result">
      {/* ───────── Breadcrumb ───────── */}
      <button onClick={() => navigate("/skills")} className="rm-tag mb-3" data-testid="result-back-btn">
        <ArrowLeft size={14} /> Skill Lab
      </button>

      {/* ───────── HEADLINE — the nugget ───────── */}
      <div
        className="rm-card-static p-4 md:p-5 mb-4"
        style={{ background: "white" }}
        data-testid="result-headline"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          {/* Left — big aim-point number */}
          <div className="md:col-span-5">
            <div className="rm-label" style={{ color: "#475569" }}>Aim for</div>
            <div
              className="font-display leading-none"
              style={{ fontSize: "clamp(2.2rem, 5vw, 3.4rem)", color: "#FF6A00" }}
              data-testid="headline-aim-point"
            >
              {fmt(sr.mid, currency)}
            </div>
            <div className="font-mono-rm text-[11px] uppercase tracking-widest mt-1" style={{ color: "#475569" }}>
              Range {fmt(sr.low, currency)} — {fmt(sr.high, currency)}
            </div>

            {/* Current-salary delta — only shown when the user provided their
                current salary on the assessment. */}
            {r.current_salary_comparison && (
              <div
                className="mt-3 inline-flex flex-col gap-0.5 px-3 py-2 rounded"
                style={{
                  background:
                    r.current_salary_comparison.position === "below_market" ? "#FEF2F2" :
                    r.current_salary_comparison.position === "above_market" ? "#F0FDF4" :
                    "#FFFBEB",
                  border: "1.5px solid " + (
                    r.current_salary_comparison.position === "below_market" ? "#DC2626" :
                    r.current_salary_comparison.position === "above_market" ? "#10B981" :
                    "#FFC145"
                  ),
                }}
                data-testid="result-salary-delta"
              >
                <div className="font-mono-rm text-[9px] uppercase tracking-widest"
                     style={{ color: "#475569", fontWeight: 700 }}>
                  Your current: {fmt(r.current_salary_comparison.current_salary, currency)}
                </div>
                <div className="font-display text-[13px] leading-tight"
                     style={{
                       color:
                         r.current_salary_comparison.position === "below_market" ? "#991B1B" :
                         r.current_salary_comparison.position === "above_market" ? "#065F46" :
                         "#92400E"
                     }}>
                  {r.current_salary_comparison.headline}
                </div>
              </div>
            )}
          </div>

          {/* Middle — verdict line */}
          <div className="md:col-span-4">
            <h1 className="font-display text-lg md:text-xl uppercase leading-tight" data-testid="headline-role">
              {data.target_role || "Salary assessment"}
            </h1>
            <div className="font-mono-rm text-[11px] uppercase tracking-widest mt-1" style={{ color: "#475569" }}>
              {data.location || "—"}
              {data.years_experience != null ? ` · ${data.years_experience}y exp` : ""}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span
                className="font-mono-rm text-[10px] uppercase tracking-widest px-2 py-0.5"
                style={{ background: demandColor, color: "white", border: "2px solid #0F1B2D" }}
                data-testid="headline-demand"
              >
                ● {ctx.demand || "unknown"} demand
              </span>
              <span
                className="font-mono-rm text-[10px] uppercase tracking-widest px-2 py-0.5"
                style={{ background: "white", color: "#0F1B2D", border: "2px solid #0F1B2D" }}
                data-testid="headline-confidence"
              >
                {sr.confidence || "medium"} confidence
              </span>
            </div>
          </div>

          {/* Right — primary actions */}
          <div className="md:col-span-3 flex md:flex-col gap-2 md:items-end">
            <button
              onClick={() => scrollToSection("section-negotiate")}
              className="rm-btn rm-btn-primary text-xs md:w-full justify-center"
              data-testid="headline-cta-negotiate"
            >
              <Target size={12} /> See negotiation
            </button>
            <button
              onClick={() => scrollToSection("section-adjacent")}
              className="rm-btn rm-btn-ghost text-xs md:w-full justify-center"
              data-testid="headline-cta-adjacent"
            >
              <Compass size={12} /> {adjacentRoles.length} adjacent roles
            </button>
          </div>
        </div>
      </div>

      {/* ───────── COLLAPSIBLE SECTIONS ─────────
          Replaced the older tab strip with stacked sections so users see
          the structure of their report up-front and can drill in without
          losing context. Overview opens by default. */}
      <SectionExpandToolbar
        group="skills-result"
        targetSelector='[data-testid="result-sections"]'
        pdfTitle={`Salary report · ${data.target_role || "Untitled role"}`}
        pdfFilename={`resmo-salary-${(data.target_role || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`}
      />
      <ReportTabStrip
        testIdPrefix="skills-tab"
        tabs={[
          { id: "overview", label: "Overview", sectionTestId: "section-overview" },
          { id: "skills", label: "Skills", count: skillValue.length, sectionTestId: "section-skills" },
          { id: "negotiate", label: "Negotiate", sectionTestId: "section-negotiate" },
          { id: "adjacent", label: "Adjacent roles", count: adjacentRoles.length, sectionTestId: "section-adjacent" },
        ]}
      />
      <div className="space-y-3" data-testid="result-sections">
        <ResultSection
          title="Overview"
          eyebrow="Salary aim point + market context"
          accentGradient="linear-gradient(135deg,#FBBF24 0%,#CA8A04 100%)"
          defaultOpen
          group="skills-result"
          testid="section-overview"
        >
          <div className="space-y-4">
            {/* Salary bar */}
            <div>
              <div className="rm-label mb-2">Where the aim point sits in the range</div>
              <div className="h-3 rounded-full relative" style={{ background: "#F1F5F9", border: "2px solid #0F1B2D" }}>
                {positions && (
                  <>
                    <div className="h-full rounded-full"
                         style={{ background: "linear-gradient(90deg, #FF6A00, #FFC145)", width: `${positions.midPct}%` }} />
                    <div className="absolute top-1/2 -translate-y-1/2"
                         style={{ left: `calc(${positions.midPct}% - 8px)` }}>
                      <div className="w-4 h-4 rounded-full" style={{ background: "white", border: "3px solid #FF6A00" }} />
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-between mt-1 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#475569" }}>
                <span>{fmt(sr.low, currency)}</span>
                <span>{fmt(sr.high, currency)}</span>
              </div>
            </div>

            <div data-testid="market-card" className="pt-2" style={{ borderTop: "1px dashed #CBD5E1" }}>
              <div className="rm-label mb-1" style={{ color: "#FF6A00" }}>What's happening out there</div>
              <p className="text-sm leading-snug">{ctx.summary || "—"}</p>
              {ctx.location_adjustment && (
                <div className="mt-2 p-2.5" style={{ background: "#FFFBEB", border: "2px solid #FFC145" }}>
                  <div className="rm-label mb-0.5">Location effect</div>
                  <p className="text-sm">{ctx.location_adjustment}</p>
                </div>
              )}
            </div>

            {sr.rationale && (
              <div className="pt-2" style={{ borderTop: "1px dashed #CBD5E1", borderLeft: "5px solid #FF6A00", paddingLeft: 12 }}>
                <div className="rm-label mb-1" style={{ color: "#FF6A00" }}>How we got here</div>
                <p className="text-sm leading-snug">{sr.rationale}</p>
              </div>
            )}
          </div>
        </ResultSection>

        <ResultSection
          title="Skills"
          eyebrow={`${skillValue.length} ranked skills`}
          accentGradient="linear-gradient(135deg,#F97316 0%,#B91C1C 100%)"
          count={skillValue.length}
          group="skills-result"
          testid="section-skills"
        >
          <div className="space-y-4">
            {/* Top-value */}
            {topSkills.length > 0 && (
              <div data-testid="top-skills-card">
                <div className="rm-label mb-2">Lead with these</div>
                <div className="flex flex-wrap gap-1.5">
                  {topSkills.map((s, i) => (
                    <span key={i} className="rm-tag" style={{ background: "#FF6A00", color: "white", borderColor: "#0F1B2D" }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Skill-by-skill matrix — rated rows, tap for detail */}
            {skillValue.length > 0 && (
              <div data-testid="skill-value-card" className="pt-3" style={{ borderTop: "1px dashed #CBD5E1" }}>
                <div className="rm-label mb-3">What each skill is paying</div>
                <div className="space-y-2">
                  {skillValue.map((s, i) => {
                    const p = s.market_premium === "high"
                      ? { label: "High Value", color: "#4CAF50" }
                      : s.market_premium === "medium"
                        ? { label: "Moderate", color: "#FF9800" }
                        : { label: "Low Premium", color: "#F4511E" };
                    return (
                      <ScoreRow
                        key={i}
                        label={p.label}
                        color={p.color}
                        title={s.skill}
                        description={s.note}
                        onClick={() => setSkillSel(i)}
                        testId={`skill-value-row-${i}`}
                      />
                    );
                  })}
                </div>
                {skillSel != null && skillValue[skillSel] && (
                  <ScoreDetail
                    open
                    onClose={() => setSkillSel(null)}
                    label={skillValue[skillSel].market_premium === "high" ? "High Value"
                      : skillValue[skillSel].market_premium === "medium" ? "Moderate" : "Low Premium"}
                    color={skillValue[skillSel].market_premium === "high" ? "#4CAF50"
                      : skillValue[skillSel].market_premium === "medium" ? "#FF9800" : "#F4511E"}
                    eyebrow="Skill value"
                    title={skillValue[skillSel].skill}
                    testId="skill-detail"
                  >
                    <p className="whitespace-pre-line">{skillValue[skillSel].note}</p>
                    <div className="mt-4 flex items-center gap-2">
                      <span className="rm-label">Your rating</span>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <div key={n} className="w-3 h-3"
                             style={{ background: n <= (skillValue[skillSel].user_rating || 0) ? "#FF6A00" : "#E5E7EB",
                                      border: "1px solid #0F1B2D" }} />
                      ))}
                    </div>
                    <div className="mt-3 font-mono-rm text-[10px] uppercase tracking-widest font-bold" style={{ color: "#64748B" }}>
                      Market premium · {skillValue[skillSel].market_premium || "—"}
                    </div>
                  </ScoreDetail>
                )}
              </div>
            )}

            {/* Develop to tier up */}
            {develop.length > 0 && (
              <div data-testid="develop-card" className="pt-3" style={{ borderTop: "1px dashed #CBD5E1" }}>
                <div className="rm-label mb-2" style={{ color: "#10B981" }}>Add these to tier up</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {develop.map((d, i) => (
                    <div key={i} className="p-2.5"
                         style={{ border: "2px solid #0F1B2D", background: "#ECFDF5" }}>
                      <div className="font-bold text-sm">+ {d.skill}</div>
                      <div className="text-xs mt-1 leading-snug">{d.why}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ResultSection>

        <ResultSection
          title="Negotiate"
          eyebrow="Anchor + honesty checks"
          accentGradient="linear-gradient(135deg,#0F1B2D 0%,#1E3A5F 100%)"
          group="skills-result"
          testid="section-negotiate"
        >
          {negotiation.length > 0 ? (
            <div className="p-4"
                 style={{ background: "#0F1B2D", color: "white", border: "2px solid #0F1B2D" }}
                 data-testid="negotiation-card">
              <div className="rm-label mb-2" style={{ color: "#FFC145" }}>Anchor at the high end</div>
              <ul className="space-y-1.5 text-sm">
                {negotiation.map((p, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="rm-text-orange flex-shrink-0">▸</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="font-mono-rm text-sm" style={{ color: "#475569" }}>
              No negotiation talking points yet for this assessment.
            </div>
          )}
          {redFlags.length > 0 && (
            <div className="p-4 mt-3"
                 style={{ background: "#FEF2F2", border: "2px solid #B91C1C" }}
                 data-testid="redflags-card">
              <div className="rm-label mb-2" style={{ color: "#991B1B" }}>Honesty checks</div>
              <ul className="space-y-1.5 text-sm">
                {redFlags.map((f, i) => (
                  <li key={i} className="flex gap-2">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: "#EF4444" }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ResultSection>

        <ResultSection
          title="Adjacent roles"
          eyebrow="Roles your skills already fit"
          accentGradient="linear-gradient(135deg,#3B82F6 0%,#1E40AF 100%)"
          count={adjacentRoles.length}
          group="skills-result"
          testid="section-adjacent"
        >
          {adjacentRoles.length === 0 ? (
            <div className="rm-card-static p-4 md:p-5 font-mono-rm text-sm" style={{ color: "#475569" }}>
              No adjacent roles identified.
            </div>
          ) : (
            <div className="space-y-2" data-testid="adjacent-roles-card">
              {adjacentRoles.map((a, i) => {
                const fit = Math.max(0, Math.min(100, Number(a.fit_pct) || 0));
                return (
                  <ScoreRow
                    key={i}
                    score={fit}
                    title={a.title}
                    description={a.why_fit}
                    onClick={() => setAdjSel(i)}
                    testId={`adjacent-role-${i}`}
                  />
                );
              })}
              {adjSel != null && adjacentRoles[adjSel] && (() => {
                const a = adjacentRoles[adjSel];
                const fit = Math.max(0, Math.min(100, Number(a.fit_pct) || 0));
                const dColor = { hot: "#EF4444", strong: "#10B981", steady: "#FFC145", soft: "#94A3B8" }[a.demand] || "#94A3B8";
                const fmtSal = (n) => n ? `${currency} ${Math.round(n).toLocaleString()}` : "—";
                return (
                  <ScoreDetail
                    open
                    onClose={() => setAdjSel(null)}
                    score={fit}
                    eyebrow="Adjacent role fit"
                    title={a.title}
                    testId="adjacent-detail"
                  >
                    {a.why_fit && <p className="whitespace-pre-line">{a.why_fit}</p>}
                    <div className="flex flex-wrap gap-1.5 items-center mt-4">
                      <span className="font-mono-rm text-[10px] uppercase tracking-widest px-1.5 py-0.5"
                            style={{ background: "white", border: "2px solid #0F1B2D" }}>
                        {fmtSal(a.salary_low)} — {fmtSal(a.salary_high)}
                      </span>
                      {a.demand && (
                        <span className="font-mono-rm text-[10px] uppercase tracking-widest px-1.5 py-0.5"
                              style={{ background: dColor, color: "white", border: "2px solid #0F1B2D" }}>
                          {a.demand} demand
                        </span>
                      )}
                    </div>
                    {Array.isArray(a.skills_to_add) && a.skills_to_add.length > 0 && (
                      <div className="mt-4">
                        <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1 font-bold" style={{ color: "#475569" }}>
                          Bridge the gap
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {a.skills_to_add.map((s, j) => (
                            <span key={j} className="font-mono-rm text-[10px] px-1.5 py-0.5"
                                  style={{ background: "#FFFBEB", border: "1px solid #0F1B2D" }}>+ {s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <button onClick={() => navigate(`/skills/role/new?role=${encodeURIComponent(a.title)}`)}
                            className="rm-btn rm-btn-primary text-xs px-3 py-2 mt-4"
                            data-testid={`research-adjacent-${adjSel}`}>
                      Research this role →
                    </button>
                    <div className="mt-4">
                      <AdjacentRoleJobsPanel
                        role={a.title}
                        location={r.location || ""}
                        country={r.country || "us"}
                        testidPrefix={`adjacent-${adjSel}`}
                      />
                    </div>
                  </ScoreDetail>
                );
              })()}
            </div>
          )}
        </ResultSection>
      </div>

      {/* ───────── Footer CTA ───────── */}
      <div className="mt-6 flex justify-center">
        <Link to="/skills/new" className="rm-btn rm-btn-ghost text-xs" data-testid="another-assessment-btn">
          Run another assessment
        </Link>
      </div>
    </div>
  );
}

export default function Skills({ mode = "list" }) {
  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 pb-20">
        {mode === "list" && <SkillsList />}
        {mode === "new" && <SkillsWizard />}
        {mode === "result" && <SkillsResult />}
        {mode === "role_new" && <RoleResearchWizard />}
        {mode === "role_result" && <RoleResearchResult />}
      </div>
    </div>
  );
}
